/**
 * AgentsScreen — full-screen agent registry + no-code builder.
 *
 * Ported from packages/web AgentBuilder.tsx. In ros-app it renders as a
 * full-page overlay (like SessionsOverlay) instead of a route, and talks to
 * the CF backend directly via the configured AgentBuilderService.
 */
import React, {useState, useEffect, useCallback} from 'react'
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip,
  CircularProgress, InputAdornment, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel,
  ListSubheader, Switch
} from '@mui/material'
import {
  Search as SearchIcon, Add as AddIcon, ArrowBack as BackIcon,
  SmartToy as AgentIcon, ChatBubble, Description, Psychology,
  Web, VerifiedUser, GridOn, Analytics, Mic, EditNote, TableChart,
  ImageSearch, Apps
} from '@mui/icons-material'
import agentBuilderService from '../services/AgentBuilderService'
import {getMascotByName, getAvailableMascots} from '@reachinsight-app/shared/src/components/R3Chat/RosMascots'
import type {AgentDefinition, ModelCatalogEntry, ToolCatalogItem, AgentFormData} from './agent-builder/types'
import {ds, sxPageBg, sxHeading, sxCard, sxPrimaryBtn, sxOutlinedBtn} from './agent-builder/theme'
import {useRosFeatures} from '../access/useRosFeatures'

type ViewMode = 'library' | 'builder' | 'catalog'

const ICON_MAP: Record<string, {component: React.ElementType; bg: string; fg: string}> = {
  chat_bubble: {component: ChatBubble, bg: 'rgba(139,92,246,0.1)', fg: '#8b5cf6'},
  description: {component: Description, bg: 'rgba(91,127,166,0.1)', fg: '#5b7fa6'},
  psychology: {component: Psychology, bg: 'rgba(217,119,87,0.1)', fg: '#d97757'},
  web: {component: Web, bg: 'rgba(196,135,59,0.1)', fg: '#c4873b'},
  verified_user: {component: VerifiedUser, bg: 'rgba(46,139,110,0.1)', fg: '#2e8b6e'},
  grid_on: {component: GridOn, bg: 'rgba(46,139,110,0.1)', fg: '#2e8b6e'},
  analytics: {component: Analytics, bg: 'rgba(46,139,110,0.1)', fg: '#2e8b6e'},
  smart_toy: {component: AgentIcon, bg: 'rgba(107,143,94,0.1)', fg: ds.sage500},
  mic: {component: Mic, bg: 'rgba(91,127,166,0.1)', fg: '#5b7fa6'},
  edit_note: {component: EditNote, bg: 'rgba(196,135,59,0.1)', fg: '#c4873b'},
  table_chart: {component: TableChart, bg: 'rgba(91,127,166,0.1)', fg: '#5b7fa6'},
  image_search: {component: ImageSearch, bg: 'rgba(139,92,246,0.1)', fg: '#8b5cf6'},
  apps: {component: Apps, bg: 'rgba(107,143,94,0.1)', fg: ds.sage500},
}

const DEFAULT_FORM: AgentFormData = {
  displayName: '',
  mascot: 'pop',
  shortDescription: '',
  detailedDescription: '',
  systemPrompt: '',
  icon: 'smart_toy',
  tools: [],
  modelProvider: 'bedrock',
  modelId: 'global.anthropic.claude-sonnet-4-6',
  temperature: 0.6,
  maxOutputTokens: 8192,
  maxToolTurns: 10,
  intentDescription: '',
  intentKeywords: [],
  acceptedFileTypes: '',
  outputFormat: 'markdown',
  sessionSticky: true,
  guardrails: {},
  accessType: 'platform',
  accessConfig: {},
}

interface AgentsScreenProps {
  apiBaseUrl: string
  token: string | null
  onClose: () => void
}

type SystemEditForm = Pick<AgentFormData, 'displayName' | 'mascot' | 'shortDescription' | 'modelProvider' | 'modelId' | 'temperature' | 'maxOutputTokens' | 'maxToolTurns'>

const CONFIGURABLE_SYSTEM_AGENT_SLUGS = new Set(['discovery', 'authoring', 'website_builder', 'crosstabs_builder'])

export function AgentsScreen({apiBaseUrl, token, onClose}: AgentsScreenProps) {
  const {authFetch, reportUnavailable} = useRosFeatures()
  // Sub-components (SystemPromptSection, TestSection) call the module singleton
  // directly, so configure it in the render body — before any effect fires.
  agentBuilderService.configure(apiBaseUrl, token, authFetch as typeof fetch, () => {
    reportUnavailable()
    onClose()
  })

  const [view, setView] = useState<ViewMode>('library')
  const [agents, setAgents] = useState<AgentDefinition[]>([])
  const [tools, setTools] = useState<ToolCatalogItem[]>([])
  const [models, setModels] = useState<ModelCatalogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [editingAgent, setEditingAgent] = useState<AgentDefinition | null>(null)
  const [form, setForm] = useState<AgentFormData>(DEFAULT_FORM)
  const [activeSection, setActiveSection] = useState(0)
  const [saving, setSaving] = useState(false)
  const [togglingSlug, setTogglingSlug] = useState<string | null>(null)
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error'}>({open: false, message: '', severity: 'success'})
  // System agent edit dialog state
  const [systemEditAgent, setSystemEditAgent] = useState<AgentDefinition | null>(null)
  const [systemEditForm, setSystemEditForm] = useState<SystemEditForm>({
    displayName: '', mascot: '', shortDescription: '', modelProvider: '', modelId: '',
    temperature: 0.7, maxOutputTokens: 8192, maxToolTurns: 10,
  })

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [agentList, toolList, modelList] = await Promise.all([
        agentBuilderService.getAgents('all'),
        agentBuilderService.getTools(),
        agentBuilderService.getModels(),
      ])
      setAgents(agentList)
      setTools(toolList)
      setModels(modelList)
    } catch (err) {
      console.error('Failed to load agent builder data:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const handleNewAgent = () => {
    setEditingAgent(null)
    setForm(DEFAULT_FORM)
    setActiveSection(0)
    setView('builder')
  }

  const handleEditAgent = (agent: AgentDefinition) => {
    if (agent.category === 'system') {
      // System agents: simple dialog for name, mascot, description
      setEditingAgent(null)
      setSystemEditAgent(agent)
      setSystemEditForm({
        displayName: agent.displayName,
        mascot: agent.mascot || 'pop',
        shortDescription: agent.shortDescription || '',
        modelProvider: agent.modelProvider,
        modelId: agent.modelId,
        temperature: agent.temperature,
        maxOutputTokens: agent.maxOutputTokens,
        maxToolTurns: agent.maxToolTurns,
      })
      return
    }
    // Custom agents: full builder form
    setSystemEditAgent(null)
    setEditingAgent(agent)
    setForm({
      displayName: agent.displayName,
      mascot: agent.mascot || 'pop',
      shortDescription: agent.shortDescription || '',
      detailedDescription: agent.detailedDescription || '',
      systemPrompt: agent.systemPrompt || '',
      icon: agent.icon || '',
      tools: agent.tools || [],
      modelProvider: agent.modelProvider,
      modelId: agent.modelId,
      temperature: agent.temperature,
      maxOutputTokens: agent.maxOutputTokens,
      maxToolTurns: agent.maxToolTurns,
      intentDescription: agent.intentDescription || '',
      intentKeywords: agent.intentKeywords || [],
      acceptedFileTypes: agent.acceptedFileTypes || '',
      outputFormat: agent.outputFormat,
      sessionSticky: agent.sessionSticky,
      guardrails: agent.guardrails,
      accessType: agent.accessType,
      accessConfig: agent.accessConfig,
    })
    setActiveSection(0)
    setView('builder')
  }

  const handleSystemEditSave = async () => {
    if (!systemEditAgent) return
    setSaving(true)
    try {
      await agentBuilderService.updateAgent(systemEditAgent.slug, systemEditForm)
      setSnackbar({open: true, message: `${systemEditForm.displayName} updated`, severity: 'success'})
      setSystemEditAgent(null)
      loadData()
    } catch (err: any) {
      setSnackbar({open: true, message: err.message || 'Failed to save', severity: 'error'})
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      if (editingAgent) {
        await agentBuilderService.updateAgent(editingAgent.slug, form)
        // Save prompt to R2 if agent uses R2 storage
        if (editingAgent.systemPromptR2Key && form.systemPrompt) {
          await agentBuilderService.savePrompt(editingAgent.slug, form.systemPrompt)
        }
        setSnackbar({open: true, message: 'Agent updated successfully', severity: 'success'})
      } else {
        const created = await agentBuilderService.createAgent(form)
        // For new agents with a prompt, save to R2
        if (form.systemPrompt) {
          await agentBuilderService.savePrompt(created.slug, form.systemPrompt)
        }
        setSnackbar({open: true, message: 'Agent created successfully', severity: 'success'})
      }
      await loadData()
      setView('library')
    } catch (err) {
      setSnackbar({open: true, message: err instanceof Error ? err.message : 'Failed to save', severity: 'error'})
    } finally {
      setSaving(false)
    }
  }

  const handleActivate = async (slug: string) => {
    try {
      await agentBuilderService.activateAgent(slug)
      setSnackbar({open: true, message: 'Agent activated and registry reloaded', severity: 'success'})
      await loadData()
    } catch (err) {
      setSnackbar({open: true, message: err instanceof Error ? err.message : 'Failed to activate', severity: 'error'})
    }
  }

  const handleAvailabilityChange = async (agent: AgentDefinition) => {
    if (agent.status !== 'active' && agent.status !== 'disabled') return
    setTogglingSlug(agent.slug)
    const enabling = agent.status === 'disabled'
    try {
      if (enabling) await agentBuilderService.activateAgent(agent.slug)
      else await agentBuilderService.deleteAgent(agent.slug)
      setAgents(current => current.map(item => item.slug === agent.slug
        ? {...item, status: enabling ? 'active' : 'disabled'}
        : item))
      setSnackbar({
        open: true,
        message: `${agent.displayName} ${enabling ? 'enabled' : 'disabled'} for everyone`,
        severity: 'success',
      })
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : `Failed to ${enabling ? 'enable' : 'disable'} agent`,
        severity: 'error',
      })
    } finally {
      setTogglingSlug(null)
    }
  }

  const filteredAgents = agents.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false
    if (search && !a.displayName.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const SECTIONS = ['Identity', 'System Prompt', 'Tools', 'Model Config', 'Intent Triggers', 'Safety', 'Access', 'Test']

  if (loading) {
    return (
      <Box sx={{...sxPageBg, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh'}}>
        <CircularProgress sx={{color: ds.sage500}} />
      </Box>
    )
  }

  // ── Library View ─────────────────────────────────────────────────────────
  if (view === 'library') {
    return (
      <Box sx={sxPageBg}>
        <Box sx={{p: 4, maxWidth: 1200, mx: 'auto'}}>
          {/* Header */}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 1}}>
            <Tooltip title="Back to Ros">
              <IconButton size="small" onClick={onClose} sx={{color: ds.textSecondary}}>
                <BackIcon />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography sx={{...sxHeading, fontSize: '1.5rem'}}>Agents</Typography>
              <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, mt: 0.5}}>
                Create and manage Ros agents. {agents.length} agents registered.
              </Typography>
            </Box>
          </Box>

          {/* Toolbar */}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mt: 3, mb: 2}}>
            <TextField
              size="small"
              placeholder="Search agents..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{fontSize: 18, color: ds.textMuted}} /></InputAdornment>,
              }}
              sx={{maxWidth: 320, '& .MuiOutlinedInput-root': {borderRadius: ds.radiusSm, fontSize: '0.8125rem'}}}
            />
            {['all', 'active', 'draft', 'disabled'].map(s => (
              <Chip
                key={s}
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                size="small"
                onClick={() => setStatusFilter(s)}
                sx={{
                  fontWeight: 500, fontSize: '0.75rem',
                  bgcolor: statusFilter === s ? ds.sage500 : ds.surface,
                  color: statusFilter === s ? '#fff' : ds.textSecondary,
                  border: `1px solid ${statusFilter === s ? ds.sage500 : ds.border}`,
                  '&:hover': {bgcolor: statusFilter === s ? ds.sage600 : ds.bgHover},
                }}
              />
            ))}
            <Box sx={{flex: 1}} />
            <Button startIcon={<AddIcon />} onClick={handleNewAgent} sx={sxPrimaryBtn}>
              New Agent
            </Button>
          </Box>

          {/* Agent Grid */}
          <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2}}>
            {filteredAgents.map(agent => {
              const mascotEntry = getMascotByName(agent.mascot)
              const MascotAnimated = mascotEntry.animated
              return (
                <Box
                  key={agent.slug}
                  onClick={() => handleEditAgent(agent)}
                  sx={{
                    ...sxCard,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': {borderColor: ds.borderStrong, boxShadow: ds.shadowSm},
                  }}
                >
                  {/* Card Header */}
                  <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5}}>
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25}}>
                      <MascotAnimated size={44} />
                      <Typography sx={{fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: ds.textMuted}}>
                        {mascotEntry.name}
                      </Typography>
                    </Box>
                    <Box sx={{flex: 1}}>
                      <Typography sx={{fontSize: '0.9375rem', fontWeight: 600}}>{agent.displayName}</Typography>
                      <Box sx={{display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap'}}>
                        <Chip
                          label={agent.status === 'coming_soon' ? 'coming soon' : agent.status === 'tool_available' ? 'studio tool' : agent.status}
                          size="small"
                          sx={{
                            height: 18, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                            bgcolor: agent.status === 'active' ? ds.successLight
                              : agent.status === 'draft' ? ds.warningLight
                              : agent.status === 'coming_soon' ? 'rgba(139,92,246,0.1)'
                              : agent.status === 'tool_available' ? 'rgba(91,127,166,0.1)'
                              : 'rgba(0,0,0,0.04)',
                            color: agent.status === 'active' ? ds.success
                              : agent.status === 'draft' ? ds.warning
                              : agent.status === 'coming_soon' ? '#8b5cf6'
                              : agent.status === 'tool_available' ? '#5b7fa6'
                              : ds.textMuted,
                          }}
                        />
                        <Chip
                          label={agent.category}
                          size="small"
                          sx={{
                            height: 18, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                            bgcolor: agent.category === 'system' ? ds.mastraBg : ds.customBg,
                            color: agent.category === 'system' ? ds.mastra : ds.custom,
                          }}
                        />
                        {agent.hasMcpApp && (
                          <Chip
                            label="MCP App"
                            size="small"
                            sx={{
                              height: 18, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                              bgcolor: 'rgba(46,139,110,0.1)', color: '#2e8b6e',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    {(agent.status === 'active' || agent.status === 'disabled') && (
                      <Tooltip title={agent.status === 'active'
                        ? 'Enabled for everyone. Turn off to remove this agent from Ros routing.'
                        : 'Disabled for everyone. Turn on to make this agent available to Ros.'}>
                        <Switch
                          size="small"
                          checked={agent.status === 'active'}
                          disabled={togglingSlug === agent.slug}
                          inputProps={{'aria-label': `${agent.status === 'active' ? 'Disable' : 'Enable'} ${agent.displayName}`}}
                          onClick={event => event.stopPropagation()}
                          onChange={() => void handleAvailabilityChange(agent)}
                        />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Description */}
                  <Typography sx={{
                    fontSize: '0.8125rem', color: ds.textSecondary, lineHeight: 1.5, mb: 1.5,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    {agent.shortDescription}
                  </Typography>

                  {/* Tool tags */}
                  {agent.tools.length > 0 && (
                    <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1}}>
                      {agent.tools.slice(0, 3).map(t => (
                        <Chip key={t} label={t} size="small" sx={{height: 20, fontSize: '0.65rem', bgcolor: ds.warm50, color: ds.textSecondary, border: `1px solid ${ds.border}`}} />
                      ))}
                      {agent.tools.length > 3 && (
                        <Chip label={`+${agent.tools.length - 3} more`} size="small" sx={{height: 20, fontSize: '0.65rem', bgcolor: ds.warm50, color: ds.textMuted}} />
                      )}
                    </Box>
                  )}

                  {/* Meta */}
                  <Typography sx={{fontSize: '0.7rem', color: ds.textMuted}}>
                    {agent.createdBy ? `By ${agent.createdBy}` : 'System'} · {new Date(agent.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>

        {/* System Agent Edit Dialog */}
        <Dialog open={!!systemEditAgent} onClose={() => !saving && setSystemEditAgent(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            {systemEditAgent && (() => {
              const m = getMascotByName(systemEditForm.mascot)
              const Animated = m.animated
              return <Animated size={40} />
            })()}
            Edit System Agent
          </DialogTitle>
          <DialogContent sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important'}}>
            <TextField
              label="Display Name"
              value={systemEditForm.displayName}
              onChange={e => setSystemEditForm(f => ({...f, displayName: e.target.value}))}
              fullWidth size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Mascot</InputLabel>
              <Select
                value={systemEditForm.mascot}
                label="Mascot"
                onChange={e => setSystemEditForm(f => ({...f, mascot: e.target.value}))}
              >
                {getAvailableMascots().map(m => {
                  const entry = getMascotByName(m.id)
                  const Anim = entry.animated
                  return (
                    <MenuItem key={m.id} value={m.id} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                      <Anim size={28} />
                      <span>{m.name}</span>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            <TextField
              label="Short Description"
              value={systemEditForm.shortDescription}
              onChange={e => setSystemEditForm(f => ({...f, shortDescription: e.target.value}))}
              fullWidth size="small" multiline rows={2}
            />
            {systemEditAgent && CONFIGURABLE_SYSTEM_AGENT_SLUGS.has(systemEditAgent.slug) && (
              <ModelSettings
                form={systemEditForm}
                setForm={setSystemEditForm}
                models={models}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSystemEditAgent(null)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSystemEditSave} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={16} /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({...s, open: false}))}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({...s, open: false}))}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    )
  }

  // ── Builder View ─────────────────────────────────────────────────────────
  return (
    <Box sx={{...sxPageBg, display: 'flex', height: '100vh'}}>
      {/* Section Sidebar */}
      <Box sx={{width: 200, bgcolor: ds.surface, borderRight: `1px solid ${ds.border}`, py: 2, flexShrink: 0}}>
        {SECTIONS.map((label, i) => (
          <Box
            key={label}
            onClick={() => setActiveSection(i)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1,
              cursor: 'pointer', fontSize: '0.8125rem', color: activeSection === i ? ds.sage700 : ds.textSecondary,
              bgcolor: activeSection === i ? ds.bgSelected : 'transparent',
              borderRight: activeSection === i ? `2px solid ${ds.sage500}` : '2px solid transparent',
              fontWeight: activeSection === i ? 500 : 400,
              '&:hover': {bgcolor: ds.bgHover},
            }}
          >
            <Box sx={{
              width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.65rem', fontWeight: 600, flexShrink: 0,
              bgcolor: activeSection === i ? ds.sage500 : ds.warm100,
              color: activeSection === i ? '#fff' : ds.textMuted,
            }}>
              {i + 1}
            </Box>
            {label}
          </Box>
        ))}
      </Box>

      {/* Main Builder Area */}
      <Box sx={{flex: 1, overflow: 'auto'}}>
        {/* Header */}
        <Box sx={{
          px: 4, py: 2, borderBottom: `1px solid ${ds.border}`, bgcolor: ds.surface,
          display: 'flex', alignItems: 'center', gap: 2, position: 'sticky', top: 0, zIndex: 10,
        }}>
          <Tooltip title="Back to library">
            <IconButton size="small" onClick={() => setView('library')} sx={{color: ds.textSecondary}}>
              <BackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography sx={{...sxHeading, fontSize: '1.1rem'}}>
            {editingAgent ? editingAgent.displayName : 'New Agent'}
          </Typography>
          {editingAgent && (
            <>
              <Chip label={editingAgent.status} size="small" sx={{
                height: 20, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                bgcolor: editingAgent.status === 'active' ? ds.successLight : ds.warningLight,
                color: editingAgent.status === 'active' ? ds.success : ds.warning,
              }} />
              <Chip label={editingAgent.harness} size="small" sx={{
                height: 20, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                bgcolor: ds.mastraBg, color: ds.mastra,
              }} />
            </>
          )}
          <Box sx={{flex: 1}} />
          <Button onClick={() => setView('library')} sx={sxOutlinedBtn}>Discard</Button>
          <Button onClick={handleSave} disabled={saving} sx={sxPrimaryBtn}>
            {saving ? <CircularProgress size={16} sx={{color: '#fff', mr: 1}} /> : null}
            {editingAgent ? 'Save Changes' : 'Create Agent'}
          </Button>
          {editingAgent && editingAgent.status !== 'active' && (
            <Button onClick={() => handleActivate(editingAgent.slug)} sx={{...sxPrimaryBtn, bgcolor: ds.success, '&:hover': {bgcolor: '#257a5e'}}}>
              Activate
            </Button>
          )}
        </Box>

        {/* Section Content */}
        <Box sx={{p: 4, maxWidth: 820}}>
          {activeSection === 0 && <IdentitySection form={form} setForm={setForm} />}
          {activeSection === 1 && <SystemPromptSection form={form} setForm={setForm} agent={editingAgent} />}
          {activeSection === 2 && <ToolsSection form={form} setForm={setForm} tools={tools} />}
          {activeSection === 3 && <ModelConfigSection form={form} setForm={setForm} models={models} />}
          {activeSection === 4 && <IntentTriggersSection form={form} setForm={setForm} />}
          {activeSection === 5 && <SafetySection form={form} setForm={setForm} />}
          {activeSection === 6 && <AccessSection form={form} setForm={setForm} />}
          {activeSection === 7 && <TestSection agent={editingAgent} />}
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({...s, open: false}))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({...s, open: false}))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  )
}

// ── Section Components (inline for now — can extract to separate files if they grow) ──

interface SectionProps {
  form: AgentFormData
  setForm: React.Dispatch<React.SetStateAction<AgentFormData>>
}

function IdentitySection({form, setForm}: SectionProps) {
  const mascotEntry = getMascotByName(form.mascot)
  const MascotPreview = mascotEntry.animated
  return (
    <>
      <Typography sx={{...sxHeading, fontSize: '1.05rem', mb: 0.5}}>Identity</Typography>
      <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, fontStyle: 'italic', fontFamily: ds.fontSerif, mb: 2}}>
        How this agent appears in Ros
      </Typography>
      <TextField fullWidth label="Display Name" value={form.displayName} onChange={e => setForm(f => ({...f, displayName: e.target.value}))} sx={{mb: 2}} size="small" />
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
        <MascotPreview size={56} />
        <FormControl sx={{flex: 1}} size="small">
          <InputLabel>Mascot</InputLabel>
          <Select
            value={form.mascot}
            label="Mascot"
            onChange={e => setForm(f => ({...f, mascot: e.target.value}))}
          >
            {getAvailableMascots().map(m => {
              const entry = getMascotByName(m.id)
              const Anim = entry.animated
              return (
                <MenuItem key={m.id} value={m.id} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <Anim size={28} />
                  <span>{m.name}</span>
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
      </Box>
      <TextField fullWidth label="Short Description" value={form.shortDescription} onChange={e => setForm(f => ({...f, shortDescription: e.target.value}))} sx={{mb: 2}} size="small" helperText="One line — shown in agent cards and intent detection" />
      <TextField fullWidth label="Detailed Description" value={form.detailedDescription} onChange={e => setForm(f => ({...f, detailedDescription: e.target.value}))} sx={{mb: 2}} size="small" multiline rows={3} />
    </>
  )
}

function SystemPromptSection({form, setForm, agent}: SectionProps & {agent?: AgentDefinition | null}) {
  const [loadingPrompt, setLoadingPrompt] = useState(false)
  const [promptSource, setPromptSource] = useState<'inline' | 'r2'>('inline')

  // Load prompt from R2 on mount if agent has an R2 key and prompt is empty
  useEffect(() => {
    if (agent?.systemPromptR2Key && !form.systemPrompt) {
      setLoadingPrompt(true)
      agentBuilderService.getPrompt(agent.slug)
        .then(data => {
          if (data.prompt) {
            setForm(f => ({...f, systemPrompt: data.prompt}))
            setPromptSource(data.source)
          }
        })
        .catch(err => console.error('Failed to load prompt:', err))
        .finally(() => setLoadingPrompt(false))
    }
  }, [agent?.slug])

  const tokenEstimate = Math.round((form.systemPrompt || '').length / 4)
  return (
    <>
      <Typography sx={{...sxHeading, fontSize: '1.05rem', mb: 0.5}}>System Prompt</Typography>
      <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, fontStyle: 'italic', fontFamily: ds.fontSerif, mb: 2}}>
        The core instructions that define this agent's expertise and behavior
      </Typography>
      <Box sx={{p: 2, bgcolor: ds.sage50, border: `1px solid ${ds.sage200}`, borderRadius: ds.radiusSm, mb: 2}}>
        <Typography sx={{fontSize: '0.75rem', fontWeight: 600, color: ds.sage700, mb: 0.5}}>Automatically handled by the platform</Typography>
        <Typography sx={{fontSize: '0.8125rem', color: ds.sage800}}>User persona context, attached file details, conversation history, and handoff detection are all injected automatically.</Typography>
      </Box>
      {promptSource === 'r2' && (
        <Box sx={{p: 1.5, bgcolor: 'rgba(91,127,166,0.08)', border: `1px solid rgba(91,127,166,0.2)`, borderRadius: ds.radiusSm, mb: 1.5}}>
          <Typography sx={{fontSize: '0.75rem', color: '#5b7fa6'}}>Loaded from R2 storage. Changes will be saved back to R2.</Typography>
        </Box>
      )}
      <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 0.5}}>
        <Typography sx={{fontSize: '0.7rem', color: ds.textMuted}}>~{tokenEstimate.toLocaleString()} tokens</Typography>
      </Box>
      {loadingPrompt ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <TextField
          fullWidth multiline rows={16} value={form.systemPrompt || ''}
          onChange={e => setForm(f => ({...f, systemPrompt: e.target.value}))}
          sx={{
            '& .MuiOutlinedInput-root': {fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: '0.8125rem', lineHeight: 1.7, bgcolor: ds.warm50},
          }}
        />
      )}
    </>
  )
}

function ToolsSection({form, setForm, tools}: SectionProps & {tools: ToolCatalogItem[]}) {
  const toggleTool = (slug: string) => {
    setForm(f => ({
      ...f,
      tools: f.tools.includes(slug) ? f.tools.filter(t => t !== slug) : [...f.tools, slug],
    }))
  }

  return (
    <>
      <Typography sx={{...sxHeading, fontSize: '1.05rem', mb: 0.5}}>Tools</Typography>
      <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, fontStyle: 'italic', fontFamily: ds.fontSerif, mb: 2}}>
        Select which tools this agent can use. The agent decides when to call them based on your system prompt.
      </Typography>
      {tools.map(tool => (
        <Box
          key={tool.slug}
          onClick={() => toggleTool(tool.slug)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
            border: `1px solid ${form.tools.includes(tool.slug) ? ds.sage400 : ds.border}`,
            bgcolor: form.tools.includes(tool.slug) ? ds.bgSelected : 'transparent',
            borderRadius: ds.radiusSm, mb: 1, cursor: 'pointer',
            '&:hover': {borderColor: ds.borderStrong, bgcolor: ds.bgHover},
          }}
        >
          <Box sx={{
            width: 18, height: 18, borderRadius: '4px', border: `2px solid ${form.tools.includes(tool.slug) ? ds.sage500 : ds.warm300}`,
            bgcolor: form.tools.includes(tool.slug) ? ds.sage500 : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, flexShrink: 0,
          }}>
            {form.tools.includes(tool.slug) && '✓'}
          </Box>
          <Box sx={{flex: 1}}>
            <Typography sx={{fontSize: '0.8125rem', fontWeight: 600}}>{tool.displayName}</Typography>
            <Typography sx={{fontSize: '0.75rem', color: ds.textSecondary, mt: 0.25}}>{tool.description}</Typography>
          </Box>
          <Chip label={tool.category} size="small" sx={{height: 18, fontSize: '0.6rem', bgcolor: ds.warm50, color: ds.textMuted}} />
        </Box>
      ))}
      <Box sx={{p: 2, bgcolor: ds.sage50, border: `1px solid ${ds.sage200}`, borderRadius: ds.radiusSm, mt: 2}}>
        <Typography sx={{fontSize: '0.75rem', fontWeight: 600, color: ds.sage700, mb: 0.5}}>Automatically available to all agents</Typography>
        <Typography sx={{fontSize: '0.8125rem', color: ds.sage800}}>
          <strong>request_handoff</strong> — routes conversation to a different agent when off-topic<br/>
          <strong>task_complete</strong> — signals the agent has finished its task
        </Typography>
      </Box>
    </>
  )
}

function ModelConfigSection({form, setForm, models}: SectionProps & {models: ModelCatalogEntry[]}) {
  return (
    <>
      <Typography sx={{...sxHeading, fontSize: '1.05rem', mb: 0.5}}>Model Configuration</Typography>
      <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, fontStyle: 'italic', fontFamily: ds.fontSerif, mb: 2}}>
        Choose the LLM and generation parameters for this agent
      </Typography>
      <ModelSettings form={form} setForm={setForm} models={models} />
    </>
  )
}

type ModelSettingsForm = Pick<AgentFormData, 'modelProvider' | 'modelId' | 'temperature' | 'maxOutputTokens' | 'maxToolTurns'>

function ModelSettings<T extends ModelSettingsForm>({
  form,
  setForm,
  models,
}: {
  form: T
  setForm: React.Dispatch<React.SetStateAction<T>>
  models: ModelCatalogEntry[]
}) {
  const selectedModel = findSelectedModel(models, form.modelProvider, form.modelId)
  const selectedId = selectedModel?.id ?? ''
  const omitTemperature = Boolean(selectedModel?.omitTemperature)

  return (
    <>
      <TextField
        select
        fullWidth
        label="Model"
        value={selectedId}
        onChange={e => {
          const next = models.find(model => model.id === e.target.value)
          if (next) setForm(current => ({...current, modelProvider: next.provider, modelId: next.modelId, maxOutputTokens: next.maxOutputTokens}))
        }}
        size="small"
        SelectProps={{
          MenuProps: {
            PaperProps: {sx: {maxHeight: 420, mt: 0.5, border: `1px solid ${ds.border}`, boxShadow: ds.shadowMd}},
            MenuListProps: {dense: true},
          },
        }}
        sx={{mb: 2}}
      >
        {!selectedId && <MenuItem value="" disabled>Select a model</MenuItem>}
        <ListSubheader disableSticky sx={{fontSize: '0.6875rem', fontWeight: 700, lineHeight: '30px', color: ds.textMuted}}>
          Proprietary
        </ListSubheader>
        {models.filter(model => !model.openWeight).map(model => (
          <MenuItem key={model.id} value={model.id} disabled={!model.available} dense>
            {model.label} ({model.vendor}){model.available ? '' : ' (not configured)'}
          </MenuItem>
        ))}
        <ListSubheader disableSticky sx={{fontSize: '0.6875rem', fontWeight: 700, lineHeight: '30px', color: ds.textMuted}}>
          Open Weight
        </ListSubheader>
        {models.filter(model => model.openWeight).map(model => (
          <MenuItem key={model.id} value={model.id} disabled={!model.available} dense>
            {model.label} ({model.vendor}){model.available ? '' : ' (not configured)'}
          </MenuItem>
        ))}
      </TextField>
      <Typography sx={{fontSize: '0.75rem', fontWeight: 600, color: ds.textSecondary, mb: 0.5}}>
        Temperature: {omitTemperature ? 'Not supported by this model' : form.temperature.toFixed(1)}
      </Typography>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
        <Typography sx={{fontSize: '0.7rem', color: ds.textMuted}}>Precise</Typography>
        <input
          type="range"
          min={0}
          max={100}
          value={form.temperature * 100}
          disabled={omitTemperature}
          onChange={e => setForm(current => ({...current, temperature: Number(e.target.value) / 100}))}
          style={{flex: 1, accentColor: ds.sage500}}
        />
        <Typography sx={{fontSize: '0.7rem', color: ds.textMuted}}>Creative</Typography>
      </Box>
      <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
        <TextField
          fullWidth
          label={selectedModel?.outputLimitBasis === 'cloudflare-context-budget' ? 'Cloudflare Context Budget' : 'Max Output Tokens'}
          type="number"
          value={selectedModel?.maxOutputTokens ?? form.maxOutputTokens}
          onChange={e => setForm(current => ({...current, maxOutputTokens: Number(e.target.value)}))}
          disabled={!!selectedModel}
          helperText={selectedModel?.outputLimitBasis === 'cloudflare-context-budget' ? 'Runtime subtracts prompt and tool tokens.' : selectedModel ? 'Managed by the model catalog.' : undefined}
          size="small"
          inputProps={{min: 1, step: 1}}
        />
        <TextField fullWidth label="Max Tool Turns" type="number" value={form.maxToolTurns} onChange={e => setForm(current => ({...current, maxToolTurns: Number(e.target.value)}))} size="small" inputProps={{min: 1, step: 1}} />
      </Box>
    </>
  )
}

function findSelectedModel(models: ModelCatalogEntry[], modelProvider: string, modelId: string): ModelCatalogEntry | null {
  const direct = models.find(model => model.provider === modelProvider && model.modelId === modelId)
  if (direct) return direct
  if (modelProvider === 'anthropic-bedrock' && ['claude-sonnet-4-6', 'claude-sonnet-4-20250514', 'us.anthropic.claude-sonnet-4-20250514'].includes(modelId)) {
    return models.find(model => model.id === 'claude-sonnet-4-6') ?? null
  }
  return null
}

function IntentTriggersSection({form, setForm}: SectionProps) {
  const [keywordInput, setKeywordInput] = useState('')

  const addKeyword = () => {
    const kw = keywordInput.trim()
    if (kw && !form.intentKeywords.includes(kw)) {
      setForm(f => ({...f, intentKeywords: [...f.intentKeywords, kw]}))
      setKeywordInput('')
    }
  }

  const removeKeyword = (kw: string) => {
    setForm(f => ({...f, intentKeywords: f.intentKeywords.filter(k => k !== kw)}))
  }

  return (
    <>
      <Typography sx={{...sxHeading, fontSize: '1.05rem', mb: 0.5}}>Intent Triggers</Typography>
      <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, fontStyle: 'italic', fontFamily: ds.fontSerif, mb: 2}}>
        How Ros recognizes when to route a user's message to this agent
      </Typography>
      <TextField fullWidth label="Intent Description (what the classifier sees)" value={form.intentDescription} onChange={e => setForm(f => ({...f, intentDescription: e.target.value}))} size="small" multiline rows={3} sx={{mb: 2}} helperText="Write this for the LLM classifier — clear, specific, and distinct from other agents." />
      <Typography sx={{fontSize: '0.75rem', fontWeight: 600, color: ds.textSecondary, mb: 0.5}}>Trigger Keywords</Typography>
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1, border: `1px solid ${ds.border}`, borderRadius: ds.radiusSm, mb: 2, minHeight: 42}}>
        {form.intentKeywords.map(kw => (
          <Chip key={kw} label={kw} size="small" onDelete={() => removeKeyword(kw)} sx={{bgcolor: ds.sage50, border: `1px solid ${ds.sage200}`, color: ds.sage700, fontSize: '0.75rem'}} />
        ))}
        <TextField
          variant="standard" size="small" placeholder="Add keyword..."
          value={keywordInput} onChange={e => setKeywordInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword() } }}
          InputProps={{disableUnderline: true}}
          sx={{minWidth: 100, flex: 1, '& input': {fontSize: '0.75rem', p: 0.5}}}
        />
      </Box>
      <TextField fullWidth label="Accepted File Types" value={form.acceptedFileTypes} onChange={e => setForm(f => ({...f, acceptedFileTypes: e.target.value}))} size="small" helperText="Comma-separated extensions (e.g. .docx, .pdf) or leave empty for no file uploads" />
    </>
  )
}

function SafetySection({form, setForm}: SectionProps) {
  const guardrails = form.guardrails
  const updateGuardrail = (key: string, value: unknown) => {
    setForm(f => ({...f, guardrails: {...f.guardrails, [key]: value}}))
  }

  return (
    <>
      <Typography sx={{...sxHeading, fontSize: '1.05rem', mb: 0.5}}>Safety</Typography>
      <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, fontStyle: 'italic', fontFamily: ds.fontSerif, mb: 2}}>
        Configure guardrails for this agent's inputs and outputs
      </Typography>
      <Box sx={{p: 2, bgcolor: ds.sage50, border: `1px solid ${ds.sage200}`, borderRadius: ds.radiusSm, mb: 3}}>
        <Typography sx={{fontSize: '0.75rem', fontWeight: 600, color: ds.sage700, mb: 0.5}}>Always-on platform guardrails</Typography>
        <Typography sx={{fontSize: '0.8125rem', color: ds.sage800}}>
          Prompt injection detection, system prompt scrubbing, and cost limits are always active and cannot be disabled.
        </Typography>
      </Box>

      {/* PII Redaction */}
      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, border: `1px solid ${ds.border}`, borderRadius: ds.radiusSm, mb: 1.5}}>
        <Box sx={{flex: 1}}>
          <Typography sx={{fontSize: '0.875rem', fontWeight: 600}}>PII Redaction</Typography>
          <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, mt: 0.5}}>
            Detect and redact personal information (email, phone, credit card) in agent responses.
          </Typography>
        </Box>
        <Box
          onClick={() => updateGuardrail('piiRedaction', !guardrails.piiRedaction)}
          sx={{
            width: 36, height: 20, borderRadius: 10, position: 'relative', cursor: 'pointer', flexShrink: 0, mt: 0.5,
            bgcolor: guardrails.piiRedaction ? ds.sage500 : ds.warm300, transition: 'background 0.2s',
            '&::after': {
              content: '""', position: 'absolute', top: 2, left: guardrails.piiRedaction ? 18 : 2,
              width: 16, height: 16, borderRadius: '50%', bgcolor: '#fff', transition: 'left 0.2s',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            },
          }}
        />
      </Box>

      {/* Content Moderation */}
      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, border: `1px solid ${ds.border}`, borderRadius: ds.radiusSm}}>
        <Box sx={{flex: 1}}>
          <Typography sx={{fontSize: '0.875rem', fontWeight: 600}}>Content Moderation</Typography>
          <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, mt: 0.5}}>
            Flag or block harmful content in agent responses.
          </Typography>
          {guardrails.contentModeration && (
            <Box sx={{mt: 1.5}}>
              <Typography sx={{fontSize: '0.7rem', fontWeight: 600, color: ds.textMuted, mb: 0.5}}>
                Sensitivity: {(guardrails.moderationThreshold ?? 0.7).toFixed(2)}
              </Typography>
              <input
                type="range" min={30} max={95}
                value={(guardrails.moderationThreshold ?? 0.7) * 100}
                onChange={e => updateGuardrail('moderationThreshold', Number(e.target.value) / 100)}
                style={{width: '100%', accentColor: ds.sage500}}
              />
            </Box>
          )}
        </Box>
        <Box
          onClick={() => updateGuardrail('contentModeration', !guardrails.contentModeration)}
          sx={{
            width: 36, height: 20, borderRadius: 10, position: 'relative', cursor: 'pointer', flexShrink: 0, mt: 0.5,
            bgcolor: guardrails.contentModeration ? ds.sage500 : ds.warm300, transition: 'background 0.2s',
            '&::after': {
              content: '""', position: 'absolute', top: 2, left: guardrails.contentModeration ? 18 : 2,
              width: 16, height: 16, borderRadius: '50%', bgcolor: '#fff', transition: 'left 0.2s',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            },
          }}
        />
      </Box>
    </>
  )
}

function AccessSection({form, setForm}: SectionProps) {
  const options = [
    {value: 'platform', icon: '🌐', title: 'Platform-wide', desc: 'All users across all organizations'},
    {value: 'org', icon: '🏢', title: 'Organization-scoped', desc: 'Only users in selected organizations'},
    {value: 'feature_flag', icon: '🚩', title: 'Feature Flag (PostHog)', desc: 'Gated behind a PostHog feature flag'},
  ]

  return (
    <>
      <Typography sx={{...sxHeading, fontSize: '1.05rem', mb: 0.5}}>Access</Typography>
      <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, fontStyle: 'italic', fontFamily: ds.fontSerif, mb: 2}}>
        Control who can use this agent in Ros
      </Typography>
      {options.map(opt => (
        <Box
          key={opt.value}
          onClick={() => setForm(f => ({...f, accessType: opt.value}))}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 1,
            border: `1px solid ${form.accessType === opt.value ? ds.sage400 : ds.border}`,
            bgcolor: form.accessType === opt.value ? ds.bgSelected : 'transparent',
            borderRadius: ds.radiusSm, cursor: 'pointer',
            '&:hover': {borderColor: ds.borderStrong},
          }}
        >
          <Typography sx={{fontSize: '1.25rem'}}>{opt.icon}</Typography>
          <Box sx={{flex: 1}}>
            <Typography sx={{fontSize: '0.8125rem', fontWeight: 500}}>{opt.title}</Typography>
            <Typography sx={{fontSize: '0.75rem', color: ds.textSecondary}}>{opt.desc}</Typography>
          </Box>
          <Box sx={{
            width: 18, height: 18, borderRadius: '50%', border: `2px solid ${form.accessType === opt.value ? ds.sage500 : ds.warm300}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {form.accessType === opt.value && <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: ds.sage500}} />}
          </Box>
        </Box>
      ))}
      {form.accessType === 'feature_flag' && (
        <TextField
          fullWidth label="PostHog Feature Flag Key" size="small" sx={{mt: 2}}
          value={(form.accessConfig as any).flagKey || ''}
          onChange={e => setForm(f => ({...f, accessConfig: {flagKey: e.target.value}}))}
          helperText="e.g. reachy-agent-name"
        />
      )}
    </>
  )
}

function TestSection({agent}: {agent: AgentDefinition | null}) {
  const [message, setMessage] = useState('')
  const [testing, setTesting] = useState(false)
  const [result, setResult] = useState<{text: string; latencyMs: number; tokenUsage: any} | null>(null)
  const [error, setError] = useState<string | null>(null)

  const runTest = async () => {
    if (!agent || !message.trim()) return
    setTesting(true)
    setResult(null)
    setError(null)
    try {
      const res = await agentBuilderService.testAgent(agent.slug, message.trim())
      if (res.error) setError(res.error)
      else setResult({text: res.text, latencyMs: res.latencyMs, tokenUsage: res.tokenUsage})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed')
    } finally {
      setTesting(false)
    }
  }

  if (!agent) {
    return (
      <>
        <Typography sx={{...sxHeading, fontSize: '1.05rem', mb: 0.5}}>Test Agent</Typography>
        <Alert severity="info" sx={{mt: 2}}>Save the agent first, then you can test it here.</Alert>
      </>
    )
  }

  return (
    <>
      <Typography sx={{...sxHeading, fontSize: '1.05rem', mb: 0.5}}>Test Agent</Typography>
      <Typography sx={{fontSize: '0.8125rem', color: ds.textSecondary, fontStyle: 'italic', fontFamily: ds.fontSerif, mb: 2}}>
        Send test messages to preview how this agent responds
      </Typography>
      <Box sx={{bgcolor: ds.warm50, border: `1px solid ${ds.border}`, borderRadius: ds.radiusLg, overflow: 'hidden'}}>
        <Box sx={{p: 1.5, bgcolor: ds.surface, borderBottom: `1px solid ${ds.border}`, display: 'flex', alignItems: 'center', gap: 1}}>
          <AgentIcon sx={{fontSize: 16, color: ds.sage500}} />
          <Typography sx={{fontSize: '0.8125rem', fontWeight: 600}}>{agent.displayName} — Sandbox</Typography>
        </Box>
        <Box sx={{p: 2, minHeight: 200, maxHeight: 350, overflow: 'auto'}}>
          {result && (
            <>
              <Box sx={{mb: 1.5, ml: 'auto', maxWidth: '85%'}}>
                <Box sx={{p: 1.5, bgcolor: ds.sage500, color: '#fff', borderRadius: `${ds.radius} ${ds.radius} 4px ${ds.radius}`, fontSize: '0.8125rem'}}>
                  {message}
                </Box>
              </Box>
              <Box sx={{mb: 1.5, maxWidth: '85%'}}>
                <Box sx={{p: 1.5, bgcolor: ds.surface, border: `1px solid ${ds.border}`, borderRadius: `${ds.radius} ${ds.radius} ${ds.radius} 4px`, fontSize: '0.8125rem', whiteSpace: 'pre-wrap'}}>
                  {result.text}
                </Box>
                <Typography sx={{fontSize: '0.65rem', color: ds.textMuted, mt: 0.5}}>
                  {result.latencyMs}ms · {result.tokenUsage?.inputTokens || '?'} in · {result.tokenUsage?.outputTokens || '?'} out
                </Typography>
              </Box>
            </>
          )}
          {error && <Alert severity="error" sx={{mt: 1}}>{error}</Alert>}
          {testing && <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}><CircularProgress size={24} sx={{color: ds.sage500}} /></Box>}
        </Box>
        <Box sx={{display: 'flex', gap: 1, p: 1.5, borderTop: `1px solid ${ds.border}`, bgcolor: ds.surface}}>
          <TextField
            fullWidth size="small" placeholder="Type a test message..."
            value={message} onChange={e => setMessage(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); runTest() } }}
            sx={{'& .MuiOutlinedInput-root': {borderRadius: ds.radiusSm, fontSize: '0.8125rem'}}}
          />
          <Button onClick={runTest} disabled={testing || !message.trim()} sx={{...sxPrimaryBtn, minWidth: 'auto', px: 1.5}}>
            Send
          </Button>
        </Box>
      </Box>
    </>
  )
}
